<template>
  <v-card class="pa-5">
    <v-form ref="form" v-model="formValid" @submit.prevent="getExcel">
      <v-row>
        <v-col cols="12" sm="4" md="4" lg="3">
          <DateTimePicker
            v-model="teamSalesStatisticsPayload.fromDate"
            :textFieldProps="{ ...textFieldProps, required: true, errorMessages: fromDateErrorMessages }"
            :datePickerProps="{ max: datePickerMaxDate }"
            :timePickerProps="{ max: fromTimePickerMaxTime }"
            :label="'From Date (GMT+' + getGmtDiff + ')'"
            @date-change="fromDateSelection = $event" />
        </v-col>
        <v-col cols="12" sm="4" md="4" lg="3">
          <DateTimePicker
            v-model="teamSalesStatisticsPayload.untilDate"
            :textFieldProps="{ ...textFieldProps, required: true, errorMessages: untilDateErrorMessages }"
            :datePickerProps="{ max: datePickerMaxDate }"
            :timePickerProps="{ max: untilTimePickerMaxTime }"
            :label="'Until Date (GMT+' + getGmtDiff + ')'"
            @date-change="untilDateSelection = $event" />
        </v-col>
        <v-col cols="12" sm="4" md="4" lg="3" class="d-flex align-center">
          <v-switch
            v-model="teamSalesStatisticsPayload.includeNotDebitedOrders"
            color="greeny"
            hide-details
            label="Include orders that have not been debited in the report."
            class="pa-0 ma-0" />
        </v-col>
        <v-col cols="12" sm="4" md="4" lg="3">
          <v-tooltip bottom :disabled="!disabledReason">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                <v-btn
                  dark
                  type="submit"
                  class="button greeny text-button download-report-btn"
                  :loading="loading"
                  :disabled="!!disabledReason">
                  <v-icon class="download-report-btn__icon"> mdi-microsoft-excel </v-icon>
                  Download Report
                </v-btn>
              </span>
            </template>
            <span>{{ disabledReason }}</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </v-form>
  </v-card>
</template>

<script>
import moment from "moment";
import DateTimePicker from "../../components/common/DateTimePicker.vue";
import Trainplanet from "@/util/trainplanet.api";
import { getErrorMessage } from "@/util/helpers";

export default {
  components: {
    DateTimePicker,
  },

  data: () => ({
    textFieldProps: {
      outlined: true,
      "hide-details": "auto",
    },
    teamSalesStatisticsPayload: {
      fromDate: new Date(
        moment()
          .utc()
          .startOf("day")
          .subtract(24, "hours")
          // adjust so that it matches Swedish timezone midnight (GMT+1 or GMT+2 in daylight savings)
          .subtract(moment().tz("Europe/Stockholm").utcOffset() / 60, "hours")
        // .subtract(moment().tz("Europe/Stockholm").isDST() ? 1 : 0, "hours")
      ),
      untilDate: new Date(
        moment()
          .utc()
          .startOf("day")
          // adjust so that it matches Swedish timezone midnight (GMT+1 or GMT+2 in daylight savings)
          .subtract(moment().tz("Europe/Stockholm").utcOffset() / 60, "hour")
        // .subtract(moment().tz("Europe/Stockholm").isDST() ? 1 : 0, "hour")
      ),
      includeNotDebitedOrders: true,
    },
    loading: false,
    formValid: true,
    fromDateSelection: null,
    untilDateSelection: null,
  }),
  computed: {
    getGmtDiff() {
      return moment().utcOffset() / 60;
    },
    fromDateErrorMessages() {
      return this.teamSalesStatisticsPayload.fromDate ? [] : ["From Date field cannot be empty"];
    },
    untilDateErrorMessages() {
      return this.teamSalesStatisticsPayload.untilDate ? [] : ["Until Date field cannot be empty"];
    },
    datePickerMaxDate() {
      return moment().format("YYYY-MM-DD");
    },
    fromTimePickerMaxTime() {
      const selectedDate =
        this.fromDateSelection || moment(this.teamSalesStatisticsPayload.fromDate).format("YYYY-MM-DD");
      return selectedDate === this.datePickerMaxDate ? moment().format("HH:mm") : undefined;
    },
    untilTimePickerMaxTime() {
      const selectedDate =
        this.untilDateSelection || moment(this.teamSalesStatisticsPayload.untilDate).format("YYYY-MM-DD");
      return selectedDate === this.datePickerMaxDate ? moment().format("HH:mm") : undefined;
    },
    isDateRangeValid() {
      const { fromDate, untilDate } = this.teamSalesStatisticsPayload;
      return moment(fromDate).isSameOrBefore(untilDate);
    },
    disabledReason() {
      const { fromDate, untilDate } = this.teamSalesStatisticsPayload;

      if (!fromDate) {
        return "Please fill the From Date.";
      }
      if (!untilDate) {
        return "Please fill the Until Date.";
      }
      if (!this.isDateRangeValid) {
        return "From Date cannot be later than Until Date.";
      }
      return "";
    },
  },
  methods: {
    async getExcel() {
      this.loading = true;

      try {
        const { fromDate, untilDate, includeNotDebitedOrders } = this.teamSalesStatisticsPayload;
        const response = await Trainplanet.getTeamSalesStatistics({
          fromDate: moment(fromDate).utc().format(),
          untilDate: moment(untilDate).utc().format(),
          includeNotDebitedOrders,
        });
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const objectUrl = URL.createObjectURL(blob);
        window.open(objectUrl);
      } catch (error) {
        await this.$store.dispatch("error", await this.getBlobErrorMessage(error));
      } finally {
        this.loading = false;
      }
    },
    async getBlobErrorMessage(error) {
      if (!(error.response?.data instanceof Blob)) {
        return getErrorMessage(error);
      }
      try {
        const { message } = JSON.parse(await error.response.data.text());
        return message || getErrorMessage(error);
      } catch {
        return getErrorMessage(error);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.download-report-btn {
  height: 56px !important;
  width: 100% !important;
  min-width: 160px !important;

  &__icon {
    margin-right: 5px;
  }

  &.v-btn.theme--dark.v-btn--disabled.v-btn--has-bg {
    background-color: #86c589 !important;
    opacity: 0.5;
    color: rgba(0, 0, 0, 0.38) !important;

    .download-report-btn__icon {
      color: rgba(0, 0, 0, 0.38) !important;
    }
  }
}
</style>
